import { Box, Card } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { getPossibleTicketUsers, postAddTicket } from '../../../api/tickets';
import { getFacilitiesAndUsersList, getTicketData, getTicketMessages, sendTicketMessage } from '../../../redux/slices/tickets';
import { addLinkedTicketToRental } from '../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../redux/store';
import Editor from '../../../reusable-components/editor';
import { useSettingsCheck } from '../../../reusable-components/settings';
import TicketMessageList from '../tickets/ticket/TicketMessageList';
import TicketButtons from '../orders/order/TicketButtons';
import TicketHeader from '../orders/order/TicketHeader';
import { ticketMessageInput } from '../tickets/ticket/newTicket/ticket-message-input-cleanup';

RentalTicket.propTypes = {
  setOpenTicket: PropTypes.func,
  openTicket: PropTypes.string,
  setAddItemsToTicket: PropTypes.func,
  addItemsToTicket: PropTypes.bool,
  selectedRows: PropTypes.array,
  setSelected: PropTypes.func,
  setCurrentTicket: PropTypes.func,
  currentTicket: PropTypes.number,
  openSidebar: PropTypes.bool,
  setOpenSidebar: PropTypes.func,
  rentalId: PropTypes.number,
  orderStatus: PropTypes.string,
};

export default function RentalTicket({
  rentalId, 
  setOpenTicket,
  openTicket,
  selectedRows,
  setAddItemsToTicket,
  addItemsToTicket,
  setSelected,
  currentTicket,
  setCurrentTicket,
  openSidebar,
  setOpenSidebar,
  orderStatus
}) {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const { data: { ticketCategories, users: usersList } } = useSelector((state) => state.tickets);
  const { mainTicket, users: usersListById, subtickets } = useSelector((state) => state.tickets.data.ticketInfoById[currentTicket]) || {};
  const { pendingRentals = [], currentRentals = [], previousRentals = [] } = useSelector((state) => state.rentals.data || {});
  const allRentals = [...pendingRentals, ...currentRentals, ...previousRentals];
  const rental = allRentals?.find(r => r.orderId === rentalId);
  const { addedBy = '', facilityId, ticketIds = []  } = rental || {};
  const { user } = useSelector((state) => state.user);

  const scrollRef = useRef(null);

  const signature = useSettingsCheck('AutoSignatureOnTickets');

  const [currentTicketMessage, setCurrentTicketMessage] = useState(signature?.show ? signature?.value : '');
  const [ticketItems, setTicketItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [shouldEditorFocus, setShouldEditorFocus] = useState(false);
  const [newUsers, setNewUsers] = useState();
  const [possibleUsers, setPossibleUsers] = useState([]);
  const [displayedTicket, setDisplayedTicket] = useState(currentTicket);
  const { users: currentUsers } = useSelector((state) => state.tickets.data.ticketById[displayedTicket]) || [];
  const { allTicketUsers } = useSelector((state) => state.tickets.data.ticketInfoById[displayedTicket]) || {};
  const { ticketMessages } = useSelector(
    (state) => state.tickets.data.ticketById[currentTicket || displayedTicket] || {}
  );

  useEffect(() => {
    const scrollScrollbarToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
        });
      }
    };
    scrollScrollbarToBottom();
  }, [ticketMessages, allTicketUsers]);

  useEffect(() => setDisplayedTicket(currentTicket), [currentTicket]);

  useEffect(() => {
    if(!openSidebar) setOpenTicket('close')
  }, [openSidebar])

  useEffect(() => {
    if(openTicket !== 'close')
    dispatch(getFacilitiesAndUsersList());
  }, [openTicket]);

  useEffect(() => {
    if (displayedTicket && openTicket !== 'close') {
      dispatch(getTicketData(displayedTicket));
    }
  }, [displayedTicket, dispatch, openTicket]);

  const usersListLookup = useMemo(() => {
    if (!usersList && !usersListById) return {};
    if (!usersList) {
      return usersListById.reduce((acc, user) => {
        acc[user.id.toLowerCase()] = user.value;
        return acc;
      }, {});
    }
    if (!usersListById) {
      return usersList.reduce((acc, user) => {
        acc[user.id.toLowerCase()] = user.value;
        return acc;
      }, {});
    }
    return [...usersList, ...usersListById].reduce((acc, user) => {
      acc[user.id.toLowerCase()] = user.value;
      return acc;
    }, {});
  }, [usersList, usersListById]);

  useEffect(() => {
    const uniqueUsers = currentUsers?.reduce((acc, user) => {
      if (!acc.some((u) => u.id === user.userEmail)) {
        acc.push({
          id: user.userEmail,
          value: `${user.userName || usersList?.find((ul) => ul.id.toLowerCase() === user.userEmail)?.value || user.userEmail
            }`,
        });
      }
      return acc;
    }, []) ?? (mainTicket ? null : addedBy && user?.email !== addedBy ? [{ id: user.email }, { id: addedBy }] : null);
    setNewUsers(
      uniqueUsers?.map((u) => ({
        value: usersListLookup[u.id.toLowerCase()] || u.value || u.id,
        id: u.id,
      }))
    );
  }, [displayedTicket, currentUsers, usersList, mainTicket, openTicket, addedBy, user.email, usersListLookup]);

  const handleSetOpenTicket = (newValue) => {
    setOpenTicket(newValue);
    if(newValue === 'close') setCurrentTicket(0);
  };

  const listOfUsers = async (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const mentionElements = doc.querySelectorAll('span.mention');
    const users = Array.from(mentionElements).map((mention) => mention.getAttribute('data-id'));
    return users;
  };

  const sendMessage = (ticketId, users) => {
    dispatch(
      sendTicketMessage({
        message: currentTicketMessage,
        ticketNumber: ticketId,
        files: attachments,
        notify: true,
        users,
        sentBy: user.email,
        fullName: `${user.firstName} ${user.lastName}`,
      })
    );
  }

  const handleSend = async () => {
    if (!currentTicketMessage && attachments?.length === 0) return;
    const usersList = await listOfUsers(currentTicketMessage);
    const currentUsers = newUsers?.map((t) => t.id);
    const combinedUsers = [...usersList, ...currentUsers];

    setNewUsers(
      combinedUsers?.map((u) => ({
        value: u?.value || usersListLookup[u?.id?.toLowerCase()] || usersListLookup[u?.toLowerCase() || u?.id || u],
        id: u?.id || u,
      }))
    );

    if(!displayedTicket) {
      postAddTicket({
        subject: `Ticket Rental ${rentalId}`,
        message: '',
        ticketRentalId: rentalId,
        facilityId,
        files: attachments,
        notify: true,
        users: [user?.email, addedBy],
        ticketCategoryId: 11
      }).then((response) => dispatch(getTicketMessages({ id: response?.data?.id }))
        .then(() => {
          dispatch(addLinkedTicketToRental({ orderId: rentalId, idToAdd: response.data.id , orderStatus}));
          setCurrentTicket(response?.data?.id);
          sendMessage(response?.data?.id, combinedUsers);
        }).catch(() => {
          enqueueSnackbar('Failed to send message. Please try again.', { variant: 'error' });
        }));
    }
    else {
      sendMessage(displayedTicket, combinedUsers);
    }

    setCurrentTicketMessage(signature?.show ? signature?.value : '');
    setAttachments([]);
  };

  useEffect(() => {
    if (selectedRows && selectedRows?.length) {
      const newItems = selectedRows
        .filter((row) => !ticketItems.some((item) => item.productId === row.itemId))
        .map((row) => ({
          productId: row.itemId,
          hyperlink: `<br><br>\n\n<li><a href="/dashboard/rentals/rental-catalog" rel="noopener noreferrer" target="_blank">PS# ${row.itemId}</a>-${row.description} | Qty: ${row.orderedQty}</li>`,
        }));
      if (newItems.length) {
        setTicketItems((prevItems) => [...prevItems, ...newItems]);
        const newHyperlinks = newItems.map((item) => item.hyperlink).join('');
        setCurrentTicketMessage((prevTicket) => newHyperlinks + prevTicket);
      }
      setShouldEditorFocus(true);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (displayedTicket !== 0) {
      dispatch(getTicketMessages({ id: displayedTicket }));
    }
  }, [dispatch, rentalId, displayedTicket]);

  const handleChangeMessage = (value) => ticketMessageInput(value, setCurrentTicketMessage);

  const handleFileInputChange = (event) => {
    const maxAllowedFiles = 5;
    if (
      event.target.files.length > maxAllowedFiles ||
      event.target.files.length + attachments?.length > maxAllowedFiles
    ) {
      alert(`Only ${maxAllowedFiles} files can be uploaded at a time.`);
      event.target.value = '';
      return;
    }
    const { files } = event.target;
    const fileList = [];
    for (let i = 0; i < files.length; i++) {
      if (attachments?.some((x) => x.name === files[i].name)) {
        alert(`File ${files[i].name} already exists.`);
      } else fileList.push(files[i]);
    }
    setAttachments((prevAttachments) => {
      if (prevAttachments === undefined) {
        return fileList;
      }
      return [...prevAttachments, ...fileList];
    });
  };

  const removeFile = (file) => () => {
    setAttachments((prevAttachments) => prevAttachments.filter((x) => x !== file));
  };

  const handleCloseTicket = () => {
    handleSetOpenTicket('close');
    setAddItemsToTicket(false);
    setCurrentTicketMessage(signature?.show ? signature?.value : '');
    setTicketItems([]);
    setSelected([]);
  }

  useEffect(() => {
    if(!mainTicket?.id && openTicket !== 'close') {
      getPossibleTicketUsers(facilityId, 11) // 11 is rental ticket category id
        .then((res) => {
          setPossibleUsers(res.data ?? [])
        })
        .catch(() => setPossibleUsers([]));
    }
  }, [mainTicket, facilityId, openTicket]);
  
  const users = useMemo(() => usersListById || possibleUsers || [], [usersListById, possibleUsers]);

  return (
    <Box
      sx={{
        borderRadius: '5px',
        height:'100%',
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      {openSidebar && openTicket !== 'close' && (
        <Card sx={{ p: 1, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
          <TicketHeader
            handleSetOpenTicket={handleSetOpenTicket}
            currentTicket={currentTicket}
            currentTicketMessage={currentTicketMessage}
            handleCloseTicket={handleCloseTicket}
            subtickets={subtickets}
            mainTicket={mainTicket}
            setDisplayedTicket={setDisplayedTicket}
            displayedTicket={displayedTicket}
            newUsers={newUsers}
            setNewUsers={setNewUsers}
          />
          <Box sx={{ display: 'flex', color: (theme) => theme.palette.grey[500], alignItems: 'center', justifyContent: 'center', mt: 1 }}>Rental</Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              flexGrow: 1,
            }}
          >
            <Box
              ref={scrollRef}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: 'auto',
                my: 1
              }}
            >
              {displayedTicket !== 0 && (<TicketMessageList ticketId={displayedTicket} />)}
            </Box>
              <Editor
                attachFile
                printChat
                verySimple
                id="ticket-reply"
                value={currentTicketMessage}
                onChange={handleChangeMessage}
                placeholder="Type a message"
                sx={{ borderColor: 'transparent', flexShrink: 0 }}
                users={users}
                sendIcon
                disableSendTooltip={'Choose a category to send'}
                disableSend={mainTicket?.ticketCategoryId === 0 && ticketCategories?.length > 0}
                handleSend={handleSend}
                handleFileInputChange={handleFileInputChange}
                addItemsToTicket={addItemsToTicket}
                setAddItemsToTicket={setAddItemsToTicket}
                displayedTicket={displayedTicket}
                currentTicket={currentTicket}
                attachments={attachments}
                removeFile={removeFile}
                shouldFocus={shouldEditorFocus}
              />
          </Box>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileInputChange} />
        </Card>
      )}
      {(openTicket === 'close' || !openSidebar) && <TicketButtons
          setCurrentTicket={setCurrentTicket}
          openTicket={openTicket}
          setCurrentTicketMessage={setCurrentTicketMessage}
          signature={signature}
          setAttachments={setAttachments}
          currentTicket={currentTicket}
          handleSetOpenTicket={handleSetOpenTicket}
          setAddItemsToTicket={setAddItemsToTicket}
          setOpenSidebar={setOpenSidebar}
          direction={openSidebar ? 'horizontal' : 'vertical'}
          rentalId = {rentalId}
          orderStatus = {orderStatus}
          linkedTickets={ticketIds}
      />}
    </Box>
  );
}
